import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Card,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
  useGetSuppliesQuery,
  useDeleteSupplyMutation,
} from "../../slices/suppliesApiSlice";
import { toast } from "react-toastify";
import { useState } from "react";

const SupplyListScreen = () => {
  // Default dates: first day of month → today
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(1);

  const [filter, setFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(today);
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useGetSuppliesQuery(pageNumber);
  const [deleteSupply, { isLoading: loadingDelete }] = useDeleteSupplyMutation();

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this supply?")) {
      try {
        await deleteSupply(id).unwrap();
        toast.success("Supply deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete supply.");
      }
    }
  };

  // Quick date filters
  const setToday = () => {
    setStartDate(today);
    setEndDate(today);
  };

  const setThisWeek = () => {
    const now = new Date();
    const firstDay = new Date(now.setDate(now.getDate() - now.getDay()));
    setStartDate(firstDay.toISOString().slice(0, 10));
    setEndDate(today);
  };

  const setThisMonth = () => {
    setStartDate(firstDayOfMonth.toISOString().slice(0, 10));
    setEndDate(today);
  };

  // Improved product filter: show ONLY matching products
  const filterSupplies = () => {
    if (!data?.supplies) return [];

    return data.supplies
      .map((supply) => {
        const date = new Date(supply.date);

        const isInRange =
          (!startDate || date >= new Date(startDate)) &&
          (!endDate || date <= new Date(endDate));

        const matchesGeneric =
          supply.note?.toLowerCase().includes(filter.toLowerCase()) ||
          supply.supplierDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
          supply.supplyItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          );

        // Filter supplyItems by product name
        const filteredItems = productFilter.trim()
          ? supply.supplyItems.filter((item) =>
              item.name.toLowerCase().includes(productFilter.toLowerCase())
            )
          : supply.supplyItems;

        return {
          ...supply,
          supplyItems: filteredItems,
          include:
            isInRange &&
            matchesGeneric &&
            filteredItems.length > 0,
        };
      })
      .filter((supply) => supply.include);
  };

  // Totals
  const calculateTotalRevenue = () =>
    filterSupplies().reduce(
      (total, supply) =>
        total +
        supply.supplyItems.reduce(
          (sub, item) => sub + item.qty * item.price,
          0
        ),
      0
    );

  const calculateTotalQty = () =>
    filterSupplies().reduce(
      (total, supply) =>
        total +
        supply.supplyItems.reduce((sub, item) => sub + item.qty, 0),
      0
    );

  const calculateTotalCost = () =>
    filterSupplies().reduce(
      (total, supply) =>
        total +
        supply.supplyItems.reduce((sub, item) => sub + item.itemCost, 0),
      0
    );

  const calculateTotalProfit = () =>
    filterSupplies().reduce(
      (total, supply) =>
        total +
        supply.supplyItems.reduce((sub, item) => sub + item.profit, 0),
      0
    );

  // Excel export
  const exportSuppliesToExcel = () => {
    const supplies = filterSupplies();
    const rows = supplies.flatMap((supply) =>
      supply.supplyItems.map((item) => ({
        Date: new Date(supply.date).toLocaleDateString(),
        Product: item.name,
        Quantity: item.qty,
        Revenue: item.qty * item.price,
        Cost: item.itemCost,
        Profit: item.profit,
        Note: supply.note || "",
        Supplier: supply.supplierDetails?.name || "",
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Supplies");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "Supplies.xlsx");
  };

  // Download all images
  const downloadAllSupplyImages = async () => {
    const zip = new JSZip();
    const folder = zip.folder("All_Supply_Images");

    const imagePromises = filterSupplies().flatMap((supply) =>
      supply.supplyItems.flatMap((item) =>
        item.images?.map(async (imagePath) => {
          try {
            const response = await fetch(imagePath.replace(/\\/g, "/"));
            const blob = await response.blob();
            const filename = imagePath.split("/").pop();
            folder.file(filename, blob);
          } catch (error) {
            console.error(`Failed to fetch ${imagePath}`, error);
          }
        }) || []
      )
    );

    await Promise.all(imagePromises);
    zip.generateAsync({ type: "blob" }).then((zipFile) => {
      saveAs(zipFile, "All_Supply_Images.zip");
    });
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Supplies</h1>

          <Button
            variant="success"
            onClick={() => exportSuppliesToExcel(filterSupplies())}
          >
            Export to Excel
          </Button>

          <Button
            variant="info"
            className="mx-2"
            onClick={downloadAllSupplyImages}
          >
            Download All Images
          </Button>
        </Col>
      </Row>

      {/* Quick Date Filters */}
      <Row className="mb-3">
        <Col md={12}>
          <Button variant="secondary" className="me-2" onClick={setToday}>
            Today
          </Button>

          <Button variant="secondary" className="me-2" onClick={setThisWeek}>
            This Week
          </Button>

          <Button variant="secondary" onClick={setThisMonth}>
            This Month
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by note or supplier"
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            placeholder="Filter by product name"
          />
        </Col>
      </Row>

      {/* Reset */}
      <Row className="mb-4">
        <Col md={12} className="text-end">
          <Button onClick={refetch}>Reset</Button>
        </Col>
      </Row>

      {/* Totals */}
      <Row className="mb-3">
        <Col md={3}>
          <h4>Total Revenue: ${calculateTotalRevenue().toFixed(2)}</h4>
        </Col>

        <Col md={3}>
          <h4>Total Qty: ${calculateTotalQty().toFixed(2)}</h4>
        </Col>

        <Col md={3}>
          <h4>Total Cost: ${calculateTotalCost().toFixed(2)}</h4>
        </Col>

        <Col md={3} className="text-end">
          <h4>Total Profit: ${calculateTotalProfit().toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Table */}
      {loadingDelete && <Loader />}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || "An error occurred"}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>REV</th>
                <th>COST</th>
                <th>PROFIT</th>
                <th>NOTE</th>
                <th>SUPPLIER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterSupplies().map((supply) =>
                supply.supplyItems.map((item) => (
                  <tr key={`${supply._id}-${item.name}`}>
                    <td>{new Date(supply.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>${item.itemCost}</td>
                    <td>${item.profit.toFixed(2)}</td>

                    <td>{supply.note || "N/A"}</td>
                    <td>{supply.supplierDetails?.name || "N/A"}</td>

                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="Product"
                          style={{
                            width: "10px",
                            height: "10px",
                            objectFit: "cover",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </td>

                    <td>
                      <Button
                        as={Link}
                        to={`/supply/${supply._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>

                      <Button
                        as={Link}
                        to={`/supply/${supply._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        as={Link}
                        to={`/supply/${supply._id}/copy`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Copy
                      </Button>

                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => deleteHandler(supply._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => setPageNumber(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default SupplyListScreen;
